import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { DeptType } from '@bisa/shared';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';

interface Department {
  id: string;
  name: string;
  code: string;
  deptType: DeptType;
  description: string;
  isActive: boolean;
  displayOrder: number;
}

interface DeptForm {
  name: string;
  code: string;
  deptType: DeptType;
  description: string;
  displayOrder: string;
}

const emptyForm: DeptForm = {
  name: '',
  code: '',
  deptType: DeptType.OPD,
  description: '',
  displayOrder: '1',
};

export function DepartmentsPage() {
  const { staff } = useAuthStore();
  const facilityId = staff!.facilityId;

  const [depts, setDepts] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [form, setForm] = useState<DeptForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet<Department[]>(`/admin/facilities/${facilityId}/departments`);
      setDepts(data.sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (d: Department) => {
    setEditing(d);
    setForm({
      name: d.name,
      code: d.code,
      deptType: d.deptType,
      description: d.description,
      displayOrder: String(d.displayOrder),
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    const body = { ...form, displayOrder: Number(form.displayOrder) };
    try {
      if (editing) {
        await apiPut(`/admin/facilities/${facilityId}/departments/${editing.id}`, body);
      } else {
        await apiPost(`/admin/facilities/${facilityId}/departments`, body);
      }
      setShowModal(false);
      await load();
    } catch (e) {
      setSaveError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d: Department) => {
    await apiPut(`/admin/facilities/${facilityId}/departments/${d.id}`, { isActive: !d.isActive });
    setDepts((prev) => prev.map((x) => x.id === d.id ? { ...x, isActive: !x.isActive } : x));
  };

  const set = (k: keyof DeptForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Departments</h1>
        <Button onClick={openCreate}>+ Add Department</Button>
      </div>

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['#', 'Name', 'Code', 'Type', 'Active', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {depts.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No departments yet.</td></tr>
              )}
              {depts.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-400">{d.displayOrder}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{d.name}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-500">{d.code}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{d.deptType}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${d.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                      {d.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => openEdit(d)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(d)}>
                      {d.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        title={editing ? 'Edit Department' : 'Add Department'}
        onClose={() => { setShowModal(false); setSaveError(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button form="dept-form" type="submit" loading={saving}>{editing ? 'Save' : 'Create'}</Button>
          </>
        }
      >
        <form id="dept-form" onSubmit={handleSubmit} className="space-y-4">
          <Input label="Department Name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
          <Input
            label="Code (unique within facility)"
            value={form.code}
            onChange={(e) => set('code', e.target.value.toUpperCase())}
            placeholder="e.g. OPD, LAB, PHARM"
            required
            disabled={!!editing}
          />
          <Select label="Department Type" value={form.deptType} onChange={(e) => set('deptType', e.target.value)}>
            {Object.values(DeptType).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
          <Input label="Display Order" type="number" value={form.displayOrder} onChange={(e) => set('displayOrder', e.target.value)} />
          <Input label="Description (optional)" value={form.description} onChange={(e) => set('description', e.target.value)} />
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </form>
      </Modal>
    </div>
  );
}
